"use client";

import { Loader2, Plus, Trash2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CustomProviderModelInput } from "@/lib/admin-platform-config";

interface ActionResult {
	success: boolean;
	error?: string;
}

// Prices are stored per-token (matching the built-in model catalog, e.g.
// "0.5e-6"), but shown/edited as dollars per 1M tokens for readability.
function perTokenToPerMillion(value: string): string {
	const n = Number(value);
	if (!value || !Number.isFinite(n)) {
		return "";
	}
	return String(Number((n * 1_000_000).toFixed(6)));
}

function perMillionToPerToken(value: string): string {
	const n = Number(value);
	if (!Number.isFinite(n)) {
		return "0";
	}
	return String(n / 1_000_000);
}

// ---------------------------------------------------------------------------
// Allowed model form
// ---------------------------------------------------------------------------

export function AllowedModelForm({
	onSubmit,
}: {
	onSubmit: (data: {
		modelId: string;
		provider: string;
		groupName: string;
	}) => Promise<ActionResult>;
}) {
	const router = useRouter();
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [modelId, setModelId] = useState("");
	const [provider, setProvider] = useState("aws-bedrock");
	const [groupName, setGroupName] = useState("");

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setLoading(true);
		const result = await onSubmit({ modelId, provider, groupName });
		setLoading(false);
		if (result.success) {
			setOpen(false);
			setModelId("");
			setProvider("aws-bedrock");
			setGroupName("");
			router.refresh();
		} else {
			setError(result.error ?? "Failed to add model");
		}
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button size="sm">
					<Plus className="h-4 w-4" />
					Add Allowed Model
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Add Allowed Model</DialogTitle>
					<DialogDescription>
						Add a built-in model to the platform allow-list. Use the canonical
						model id (e.g. claude-sonnet-4-6).
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="provider">Provider</Label>
						<Input
							id="provider"
							value={provider}
							onChange={(e) => setProvider(e.target.value)}
							placeholder="aws-bedrock"
							required
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="modelId">Model ID</Label>
						<Input
							id="modelId"
							value={modelId}
							onChange={(e) => setModelId(e.target.value)}
							placeholder="claude-sonnet-4-6"
							required
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="groupName">Group</Label>
						<Input
							id="groupName"
							value={groupName}
							onChange={(e) => setGroupName(e.target.value)}
							placeholder="Claude Sonnet"
							required
						/>
					</div>
					{error && (
						<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
							{error}
						</div>
					)}
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => setOpen(false)}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={loading}>
							{loading && <Loader2 className="h-4 w-4 animate-spin" />}
							Add
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Custom provider form (create + edit)
// ---------------------------------------------------------------------------

interface CustomProviderInitial {
	id: string;
	prefix: string;
	displayName: string;
	baseUrl: string;
	models: CustomProviderModelInput[];
}

export function CustomProviderForm({
	initial,
	onCreate,
	onUpdate,
}: {
	initial?: CustomProviderInitial;
	onCreate?: (data: {
		prefix: string;
		displayName: string;
		baseUrl: string;
		apiKey: string;
		models: CustomProviderModelInput[];
	}) => Promise<ActionResult>;
	onUpdate?: (
		id: string,
		data: {
			prefix: string;
			displayName: string;
			baseUrl: string;
			apiKey?: string;
			models: CustomProviderModelInput[];
		},
	) => Promise<ActionResult>;
}) {
	const router = useRouter();
	const isEdit = Boolean(initial);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [prefix, setPrefix] = useState(initial?.prefix ?? "");
	const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
	const [baseUrl, setBaseUrl] = useState(initial?.baseUrl ?? "");
	const [apiKey, setApiKey] = useState("");
	const [models, setModels] = useState<CustomProviderModelInput[]>(
		(initial?.models ?? []).map((m) => ({
			id: m.id,
			name: m.name,
			inputPrice: perTokenToPerMillion(m.inputPrice),
			outputPrice: perTokenToPerMillion(m.outputPrice),
		})),
	);

	const addModel = () =>
		setModels((m) => [
			...m,
			{ id: "", name: "", inputPrice: "0", outputPrice: "0" },
		]);
	const removeModel = (i: number) =>
		setModels((m) => m.filter((_, idx) => idx !== i));
	const updateModel = (
		i: number,
		field: keyof CustomProviderModelInput,
		value: string,
	) =>
		setModels((m) =>
			m.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)),
		);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!isEdit && !apiKey) {
			setError("API key is required");
			return;
		}
		for (const m of models) {
			if (!m.id || !m.name) {
				setError("Each model needs an id and name");
				return;
			}
		}

		setLoading(true);
		const convertedModels: CustomProviderModelInput[] = models.map((m) => ({
			id: m.id,
			name: m.name,
			inputPrice: perMillionToPerToken(m.inputPrice),
			outputPrice: perMillionToPerToken(m.outputPrice),
		}));
		let result: ActionResult;
		if (isEdit && initial && onUpdate) {
			// Omit apiKey when left blank so the backend keeps the current one.
			result = await onUpdate(initial.id, {
				prefix,
				displayName,
				baseUrl,
				models: convertedModels,
				...(apiKey ? { apiKey } : {}),
			});
		} else if (onCreate) {
			result = await onCreate({
				prefix,
				displayName,
				baseUrl,
				apiKey,
				models: convertedModels,
			});
		} else {
			result = { success: false, error: "No handler" };
		}
		setLoading(false);

		if (result.success) {
			setOpen(false);
			if (!isEdit) {
				setPrefix("");
				setDisplayName("");
				setBaseUrl("");
				setApiKey("");
				setModels([]);
			} else {
				setApiKey("");
			}
			router.refresh();
		} else {
			setError(result.error ?? "Failed to save provider");
		}
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				{isEdit ? (
					<Button variant="outline" size="sm">
						Edit
					</Button>
				) : (
					<Button size="sm">
						<Plus className="h-4 w-4" />
						Add Custom Provider
					</Button>
				)}
			</DialogTrigger>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>
						{isEdit ? "Edit Custom Provider" : "Add Custom Provider"}
					</DialogTitle>
					<DialogDescription>
						Platform-level OpenAI-compatible provider. Users call models as
						&quot;prefix/model-id&quot;.
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="grid grid-cols-2 gap-3">
						<div className="space-y-2">
							<Label htmlFor="prefix">Prefix</Label>
							<Input
								id="prefix"
								value={prefix}
								onChange={(e) => setPrefix(e.target.value)}
								placeholder="biaopu"
								required
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="displayName">Display Name</Label>
							<Input
								id="displayName"
								value={displayName}
								onChange={(e) => setDisplayName(e.target.value)}
								placeholder="Biaopu Cloud"
								required
							/>
						</div>
					</div>
					<div className="space-y-2">
						<Label htmlFor="baseUrl">Base URL</Label>
						<Input
							id="baseUrl"
							value={baseUrl}
							onChange={(e) => setBaseUrl(e.target.value)}
							placeholder="https://hub.biaopu.cloud/v1"
							required
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="apiKey">
							API Key {isEdit && "(leave blank to keep current)"}
						</Label>
						<Input
							id="apiKey"
							type="password"
							value={apiKey}
							onChange={(e) => setApiKey(e.target.value)}
							placeholder="sk-..."
						/>
					</div>

					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label>Models</Label>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={addModel}
							>
								<Plus className="h-3 w-3" />
								Add Model
							</Button>
						</div>
						{models.length === 0 && (
							<p className="text-xs text-muted-foreground">
								No models yet. Add at least one.
							</p>
						)}
						{models.map((m, i) => (
							<div key={i} className="flex items-end gap-2">
								<div className="flex-1 space-y-1">
									<Label className="text-[10px]">Model ID</Label>
									<Input
										value={m.id}
										onChange={(e) => updateModel(i, "id", e.target.value)}
										placeholder="deepseek-v3"
									/>
								</div>
								<div className="flex-1 space-y-1">
									<Label className="text-[10px]">Name</Label>
									<Input
										value={m.name}
										onChange={(e) => updateModel(i, "name", e.target.value)}
										placeholder="DeepSeek V3"
									/>
								</div>
								<div className="w-28 space-y-1">
									<Label className="text-[10px]">Input Price</Label>
									<div className="flex items-center gap-1">
										<span className="text-xs text-muted-foreground">$</span>
										<Input
											value={m.inputPrice}
											onChange={(e) =>
												updateModel(i, "inputPrice", e.target.value)
											}
											placeholder="0.50"
										/>
										<span className="text-xs text-muted-foreground">/M</span>
									</div>
								</div>
								<div className="w-28 space-y-1">
									<Label className="text-[10px]">Output Price</Label>
									<div className="flex items-center gap-1">
										<span className="text-xs text-muted-foreground">$</span>
										<Input
											value={m.outputPrice}
											onChange={(e) =>
												updateModel(i, "outputPrice", e.target.value)
											}
											placeholder="1.50"
										/>
										<span className="text-xs text-muted-foreground">/M</span>
									</div>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="icon-sm"
									className="text-destructive"
									onClick={() => removeModel(i)}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
						))}
					</div>

					{error && (
						<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
							{error}
						</div>
					)}
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => setOpen(false)}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={loading}>
							{loading && <Loader2 className="h-4 w-4 animate-spin" />}
							{isEdit ? "Save" : "Create"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Delete button
// ---------------------------------------------------------------------------

export function DeletePlatformItemButton({
	id,
	onDelete,
	confirmMessage,
}: {
	id: string;
	onDelete: (id: string) => Promise<{ success: boolean }>;
	confirmMessage: string;
}) {
	const router = useRouter();
	const [loading, setLoading] = useState(false);

	const handleDelete = async () => {
		if (!confirm(confirmMessage)) {
			return;
		}
		setLoading(true);
		const result = await onDelete(id);
		setLoading(false);
		if (result.success) {
			router.refresh();
		}
	};

	return (
		<Button
			variant="ghost"
			size="icon-sm"
			onClick={handleDelete}
			disabled={loading}
			className="text-destructive hover:text-destructive"
		>
			{loading ? (
				<Loader2 className="h-4 w-4 animate-spin" />
			) : (
				<Trash2 className="h-4 w-4" />
			)}
		</Button>
	);
}
